<template>
	<div>
		<!-- The one way into the assistant on every signed-in page: a floating button at the bottom right, hidden while
		     the drawer is open. A labelled pill on desktop, a round icon button on phones. -->
		<transition name="assistant-launcher">
			<button
				v-if="!open"
				type="button"
				:class="['assistant-launcher', { compact: isMobile }]"
				aria-label="Open the assistant"
				@click="$store.dispatch('assistant/toggle')"
			>
				<v-icon class="assistant-launcher-icon">mdi-creation</v-icon>
				<span v-if="!isMobile" class="assistant-launcher-label">Ask the assistant</span>
			</button>
		</transition>
		<v-navigation-drawer
			:value="open"
			@input="(v) => (v ? null : close())"
			right
			app
			stateless
			hide-overlay
			disable-route-watcher
			:temporary="isMobile"
			:width="isMobile ? '100%' : drawerWidth"
			class="assistant-drawer"
		>
			<!-- stateless and hide-overlay: no scrim, no close on outside clicks, so the user can keep working the page while
			     the drawer covers its right edge (App.vue forces v-main padding to zero, so the layout cannot shrink; the
			     drawer overlays, by design); disable-route-watcher: stays open across navigation. On phones it is a
			     full-width temporary drawer. -->
			<div class="assistant-header">
				<div class="assistant-header-row">
					<div class="assistant-title">{{ view === "list" ? "Conversations" : "Assistant" }}</div>
					<div class="assistant-header-right">
						<template v-if="view === 'list'">
							<AssistantIconButton
								tip="Back to the conversation"
								icon
								small
								aria-label="Back to the conversation"
								@click="showChat"
							>
								<v-icon small>mdi-arrow-left</v-icon>
							</AssistantIconButton>
						</template>
						<template v-else>
							<AssistantIconButton
								tip="Conversations"
								icon
								small
								aria-label="Conversations"
								@click="showList"
							>
								<v-icon small>mdi-history</v-icon>
							</AssistantIconButton>
							<v-btn
								v-if="messages.length"
								small
								text
								class="assistant-header-action"
								:disabled="streaming"
								@click="newConversation"
							>
								<v-icon small class="mr-1">mdi-plus</v-icon>
								New conversation
							</v-btn>
						</template>
						<AssistantIconButton
							tip="Close"
							icon
							small
							aria-label="Close the assistant"
							@click="close"
						>
							<v-icon small>mdi-close</v-icon>
						</AssistantIconButton>
					</div>
				</div>
				<v-tooltip
					v-if="view === 'chat' && subtitle"
					bottom
					open-delay="350"
					:z-index="10010"
					content-class="assistant-tooltip"
				>
					<template #activator="{ on, attrs }">
						<div class="assistant-subtitle" v-bind="attrs" v-on="on">{{ subtitle }}</div>
					</template>
					<span>{{ subtitle }}</span>
				</v-tooltip>
			</div>

			<div v-if="view === 'list'" class="assistant-list-pane">
				<div v-if="listError" class="assistant-notice assistant-error assistant-list-error">
					<span>{{ listError }}</span>
				</div>
				<ConversationList />
			</div>
			<div v-else ref="transcript" class="assistant-transcript" tabindex="-1">
				<div v-if="messages.length === 0" class="assistant-empty">
					<p class="assistant-empty-title">What do you need to find?</p>
					<p class="assistant-empty-text">
						Products, vendors, cross references, formulation documents, the glossary and the FAQ. Every
						answer comes from Chemformation records and links back to the search form or the document.
					</p>
					<template v-if="!readOnly">
						<p class="assistant-empty-label">Try one of these</p>
						<button
							v-for="prompt in examplePrompts"
							:key="prompt"
							type="button"
							class="assistant-example"
							@click="send(prompt)"
						>
							{{ prompt }}
						</button>
					</template>
					<p class="assistant-empty-notice">
						Conversations are saved to your account and may be reviewed by Chemformation to improve the
						service, and deleting one removes its messages.
					</p>
				</div>
				<AssistantMessage
					v-for="m in messages"
					:key="m.id"
					:message="m"
					:streaming="streaming && m.id === lastId"
					:progress="progress"
					:on-product-search="onProductSearch"
					:show-suggestions="!streaming && !readOnly && m.id === lastId"
					@apply-search="applySearch"
					@suggest="sendSuggestion"
				/>
				<div v-if="error" class="assistant-notice assistant-error">
					<span>{{ error.text }}</span>
					<v-btn v-if="error.retryable" x-small text color="primary" @click="send(error.lastText, true)">
						Retry
					</v-btn>
				</div>
			</div>

			<div v-if="view === 'chat'" class="assistant-composer">
				<div v-if="readOnly" class="assistant-notice">
					<span>This conversation is closed.</span>
					<v-btn x-small text color="primary" @click="continueInNew">
						Continue in a new conversation
					</v-btn>
				</div>
				<div v-else-if="notice" class="assistant-notice">
					<span>{{ notice.text }}</span>
					<v-btn v-if="notice.kind === 'closed'" x-small text color="primary" @click="newConversation">
						New conversation
					</v-btn>
				</div>
				<div v-else class="assistant-composer-box">
					<v-textarea
						ref="composer"
						v-model="draft"
						rows="1"
						auto-grow
						dense
						solo
						flat
						hide-details
						background-color="transparent"
						placeholder="Ask about products, vendors, documents..."
						aria-label="Message the assistant"
						:disabled="streaming"
						maxlength="2000"
						@keydown.enter.exact.prevent="send(draft)"
					></v-textarea>
					<div class="assistant-composer-footer">
						<span v-if="streaming || !usageText" class="assistant-usage">
							{{ streaming ? "Working" : "" }}
						</span>
						<!-- The drawer's styled tooltip says what counts; focusable so keyboard users get it too. Centred on
						     the text it would hang past the drawer's left edge, so it is nudged right to stay inside. -->
						<v-tooltip
							v-else
							top
							open-delay="350"
							:nudge-right="usageTipNudge"
							:z-index="10010"
							content-class="assistant-tooltip"
						>
							<template #activator="{ on, attrs }">
								<span
									class="assistant-usage"
									tabindex="0"
									v-bind="attrs"
									@mouseenter="placeUsageTip"
									@focus="placeUsageTip"
									v-on="on"
								>
									{{ usageText }}
								</span>
							</template>
							<span>{{ usageTip }}</span>
						</v-tooltip>
						<AssistantIconButton
							v-if="streaming"
							tip="Stop"
							placement="top"
							fab
							x-small
							depressed
							outlined
							color="primary"
							aria-label="Stop the answer"
							@click="stop"
						>
							<v-icon small>mdi-stop</v-icon>
						</AssistantIconButton>
						<AssistantIconButton
							v-else
							tip="Send (Enter)"
							placement="top"
							fab
							x-small
							depressed
							color="primary"
							aria-label="Send the message"
							:disabled="!draft.trim()"
							@click="send(draft)"
						>
							<v-icon small>mdi-arrow-up</v-icon>
						</AssistantIconButton>
					</div>
				</div>
			</div>
		</v-navigation-drawer>
	</div>
</template>

<script>
import { mapState } from "vuex";
import AssistantMessage from "./AssistantMessage.vue";
import ConversationList from "./ConversationList.vue";
import AssistantIconButton from "./AssistantIconButton.vue";

const GENERAL_PROMPTS = [
	"Find vendors of acetone (CAS 67-64-1) in North America.",
	"Which VOC exempt, non-HAP solvents have a flash point above 100 F?",
	"What does HLB mean?",
	"How do I add Chemformation to my phone's home screen?",
];
// Page prompts that depend on page state are built in examplePrompts; these are the static ones.
const PAGE_PROMPTS = {
	Formulations: ["Find formulation documents for a hard-surface degreaser."],
};

export default {
	name: "AssistantDrawer",
	components: { AssistantMessage, ConversationList, AssistantIconButton },
	data() {
		// focusAfterTurn: a suggestion was tapped, so its button is gone; focus returns to the composer when the turn ends.
		// usageTipNudge: how far the usage tooltip moves right to stay inside the drawer (see placeUsageTip).
		return { draft: "", focusAfterTurn: false, usageTipNudge: 0 };
	},
	computed: {
		...mapState("assistant", [
			"open",
			"messages",
			"streaming",
			"progress",
			"usage",
			"notice",
			"error",
			"openProduct",
			"formDirty",
			"lastSearch",
			"view",
			"readOnly",
			"title",
			"pendingTitle",
			"listError",
		]),
		// After "Continue in a new conversation" the closed conversation's title carries over until the first send.
		subtitle() {
			return this.title || this.pendingTitle;
		},
		isMobile() {
			return this.$vuetify.breakpoint.smAndDown;
		},
		// Wide enough for a four-column comparison table without crowding the page: 460 on laptops, 520 on
		// desktops, 560 on large screens. App.vue reads the same value through --assistant-width.
		drawerWidth() {
			const b = this.$vuetify.breakpoint;
			return b.xlOnly ? 560 : b.lgAndUp ? 520 : 460;
		},
		onProductSearch() {
			return this.$route.name === "ProductSearch";
		},
		usageText() {
			const u = this.usage;
			if (!u) return "";
			// The client says "questions" where HQ says "turns": one question and its answer is one turn. The noun follows
			// the limit, so "1 of 150 questions left" and "1 of 1 question left" both read naturally.
			if (u.exempt) return "Unlimited questions";
			if (u.limit === null || u.limit === undefined) return "";
			const resets = u.resetsOn ? `, resets ${this.moment(u.resetsOn).format("MMM D")}` : "";
			return `${u.remaining} of ${u.limit} ${
				u.limit === 1 ? "question" : "questions"
			} left this month${resets}`;
		},
		usageTip() {
			return "Each question and its answer count once. Answers that fail with an error don't count.";
		},
		lastId() {
			const last = this.messages[this.messages.length - 1];
			return last ? last.id : null;
		},
		crossReferenceId() {
			const id = parseInt(this.$route.query && this.$route.query.crossReferenceId, 10);
			return id > 0 ? id : null;
		},
		examplePrompts() {
			const page = [];
			if (this.$route.name === "ProductSearch") {
				// Only offer what the model can act on: an open product, or a search that has been run.
				if (this.openProduct)
					page.push(
						`Find a second source for ${this.openProduct.name || "the product I am looking at"}.`
					);
				if (this.formDirty || this.lastSearch)
					page.push("Compare the data sheets of the top two results.");
			}
			if (this.$route.name === "Cross Reference Preview" && this.crossReferenceId)
				page.push("Summarise this cross reference as a table and point out the outliers.");
			return [...page, ...(PAGE_PROMPTS[this.$route.name] || []), ...GENERAL_PROMPTS].slice(0, 4);
		},
	},
	watch: {
		messages: {
			deep: true,
			handler() {
				this.scrollToBottom();
			},
		},
		// The transcript is re-created when the list closes; start it at the latest message again.
		view(value) {
			if (value === "chat") this.scrollToBottom();
		},
		open(value) {
			if (value && !this.usage) this.$store.dispatch("assistant/loadUsage");
			this.syncLayoutClass();
		},
		isMobile() {
			this.syncLayoutClass();
		},
		// Typing in the composer dismisses the suggested replies and keeps late ones from appearing; only the
		// empty and typed transitions are reported.
		draft(value, old) {
			const empty = !(value || "").trim();
			if (empty !== !(old || "").trim()) this.$store.dispatch("assistant/setDraftEmpty", empty);
		},
		streaming(value) {
			if (value || !this.focusAfterTurn) return;
			this.focusAfterTurn = false;
			this.focusComposer();
		},
		drawerWidth() {
			this.syncLayoutClass();
		},
		// Access was revoked mid-session: the notice says so until the user navigates, then the drawer is cleared and hidden.
		$route() {
			if (this.notice && this.notice.kind === "off") this.$store.dispatch("assistant/applyRevokedAccess");
		},
	},
	mounted() {
		this.syncLayoutClass();
	},
	beforeDestroy() {
		document.documentElement.classList.remove("assistant-open");
	},
	methods: {
		// On desktop the drawer overlays the right edge; the class lets App.vue shift the signed-in menu left so
		// the Logout link stays reachable while it is open.
		syncLayoutClass() {
			const open = this.open && !this.isMobile;
			const was = document.documentElement.classList.contains("assistant-open");
			document.documentElement.style.setProperty("--assistant-width", `${this.drawerWidth}px`);
			document.documentElement.classList.toggle("assistant-open", open);
			// The menu may wrap to a second row; the navigation re-measures its height on resize.
			if (was !== open) this.$nextTick(() => window.dispatchEvent(new Event("resize")));
		},
		scrollToBottom() {
			this.$nextTick(() => {
				const el = this.$refs.transcript;
				// The empty state reads from its heading; a transcript reads from its latest message.
				if (el) el.scrollTop = this.messages.length ? el.scrollHeight : 0;
			});
		},
		close() {
			this.$store.dispatch("assistant/close");
		},
		newConversation() {
			this.$store.dispatch("assistant/newConversation");
		},
		stop() {
			this.$store.dispatch("assistant/stop");
		},
		showList() {
			this.$store.dispatch("assistant/showList");
		},
		showChat() {
			this.$store.dispatch("assistant/showChat");
		},
		continueInNew() {
			this.$store.dispatch("assistant/continueInNew");
		},
		// The tapped button disappears with the send, so focus moves to the transcript (never the page body) and back
		// to the composer when the turn ends.
		async sendSuggestion(reply) {
			if (this.$refs.transcript) this.$refs.transcript.focus({ preventScroll: true });
			this.focusAfterTurn = true;
			await this.send(reply);
			// A send that failed before streaming started never ends a turn; restore focus here instead.
			if (this.focusAfterTurn && !this.streaming) {
				this.focusAfterTurn = false;
				this.focusComposer();
			}
		},
		// Vuetify centres a top tooltip on its activator. The usage text sits at the drawer's left edge, so the tooltip
		// (at most 280px wide, the assistant-tooltip max-width) is nudged right until its left edge is 8px inside the
		// drawer. Runs before Vuetify's own listener, and the open delay leaves time for the new nudge to apply.
		placeUsageTip(event) {
			const el = event.currentTarget;
			const drawer = el && el.closest(".assistant-drawer");
			if (!drawer) return;
			const text = el.getBoundingClientRect();
			const center = text.left + text.width / 2 - drawer.getBoundingClientRect().left;
			this.usageTipNudge = Math.max(0, Math.ceil(280 / 2 + 8 - center));
		},
		focusComposer() {
			this.$nextTick(() => {
				const input = this.$refs.composer && this.$refs.composer.$refs.input;
				if (input) input.focus();
				else if (this.$refs.transcript) this.$refs.transcript.focus();
			});
		},
		async send(text, retry = false) {
			const value = (text || "").trim();
			if (!value) return;
			this.draft = "";
			await this.$store.dispatch("assistant/send", {
				text: value,
				route: this.$route.name,
				crossReferenceId: this.crossReferenceId,
				retry,
			});
		},
		applySearch(card) {
			if (this.onProductSearch) {
				this.$store.commit("assistant/set_last_search", {
					filters: card.filters,
					totalCount: card.totalCount,
					note: card.note,
					at: Date.now(),
				});
			} else {
				this.$store.dispatch("assistant/showInSearch", card.filters);
				this.$router.push("/product-search");
			}
		},
	},
};
</script>

<style lang="scss">
// The drawer is a research notebook pinned beside the workbench, in the application's own voice: Roboto, black on
// white, the thin grey rules the forms use, the blue tint (#eff5ff, the Vuetify accent) for the user's own words and
// the logo red for one thing only, the working indicator. Assistant replies read as document text at full width;
// the user's question is the only boxed element, so the eye finds the questions and reads the answers in between.
$assistant-ink: #000;
$assistant-muted: #616161;
$assistant-rule: #e0e0e0;
$assistant-tint: #eff5ff;
$assistant-primary: #1264f6;
$assistant-mark: #ba0c2f;

// The launcher: the brand blue, a soft lift, and one motion (it rises in when the drawer closes).
.assistant-launcher {
	position: fixed;
	right: 24px;
	bottom: 24px;
	z-index: 9998; // under the navigation (9999) menus and the drawer (10000)
	display: inline-flex;
	align-items: center;
	gap: 8px;
	height: 48px;
	padding: 0 20px 0 16px;
	border-radius: 24px;
	border: 0;
	background: $assistant-primary;
	color: #fff;
	font-family: inherit;
	font-size: 1.5rem;
	font-weight: 500;
	letter-spacing: 0.01em;
	cursor: pointer;
	box-shadow: 0 4px 14px rgba(18, 100, 246, 0.35), 0 1px 3px rgba(0, 0, 0, 0.18);
	transition: box-shadow 150ms ease, transform 150ms ease, background-color 150ms ease;
	&:hover {
		background: darken($assistant-primary, 6%);
		box-shadow: 0 6px 18px rgba(18, 100, 246, 0.42), 0 2px 4px rgba(0, 0, 0, 0.2);
		transform: translateY(-1px);
	}
	&:focus-visible {
		outline: 3px solid rgba(18, 100, 246, 0.4);
		outline-offset: 3px;
	}
	&.compact {
		width: 56px;
		height: 56px;
		padding: 0;
		justify-content: center;
		border-radius: 50%;
		right: 16px;
		bottom: 16px;
	}
	.assistant-launcher-icon {
		color: #fff;
		font-size: 2.2rem;
	}
}
.assistant-launcher-enter-active,
.assistant-launcher-leave-active {
	transition: opacity 160ms ease, transform 160ms ease;
}
.assistant-launcher-enter,
.assistant-launcher-leave-to {
	opacity: 0;
	transform: translateY(8px);
}
@media (prefers-reduced-motion: reduce) {
	.assistant-launcher,
	.assistant-launcher-enter-active,
	.assistant-launcher-leave-active {
		transition: none;
	}
}
@media print {
	.assistant-launcher {
		display: none;
	}
}

// Scrollbars inside the drawer: thin, no track, a soft thumb that darkens on hover. Overrides the site's grey
// boxed scrollbar (sass/base/base.scss) for the transcript, wide tables and the conversation list.
.assistant-drawer,
.assistant-drawer * {
	scrollbar-width: thin;
	scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}
.assistant-drawer ::-webkit-scrollbar {
	width: 8px;
	height: 8px;
	background: transparent;
	border: 0;
}
.assistant-drawer ::-webkit-scrollbar-thumb {
	background: rgba(0, 0, 0, 0.18);
	border: 2px solid transparent;
	border-radius: 8px;
	background-clip: padding-box;
	box-shadow: none;
	&:hover {
		background-color: rgba(0, 0, 0, 0.32);
	}
}
.assistant-drawer ::-webkit-scrollbar-track,
.assistant-drawer ::-webkit-scrollbar-corner {
	background: transparent;
}

.assistant-drawer {
	z-index: 10000 !important; // above .navigation (9999)
	display: flex;
	flex-direction: column;
	color: $assistant-ink;
	// The site sets the root font to 10px (sass/base/base.scss), so Vuetify's rem-based helpers and
	// buttons would render at 7 to 9px in here; the drawer sets its own sizes on that 10px scale.
	font-size: 1.4rem;
	line-height: 1.5;
	.v-navigation-drawer__content {
		display: flex;
		flex-direction: column;
		height: 100%;
	}
	.v-btn {
		font-size: 1.3rem;
		text-transform: none;
		letter-spacing: normal;
	}
	.v-btn.v-size--small {
		font-size: 1.3rem;
	}
	.v-btn.v-size--x-small {
		font-size: 1.2rem;
	}
	.v-chip.v-size--x-small {
		font-size: 1.1rem;
		height: 2.2rem;
	}
	.v-icon {
		font-size: 1.8rem;
	}
	.v-btn .v-icon {
		font-size: 1.8rem;
	}
	textarea {
		font-size: 1.5rem;
		line-height: 1.45;
		color: $assistant-ink;
	}
}

.assistant-header {
	padding: 6px 8px 6px 16px;
	border-bottom: 1px solid $assistant-rule;
	.assistant-header-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		min-height: 36px;
	}
	.assistant-subtitle {
		font-size: 1.2rem;
		line-height: 1.4;
		color: $assistant-muted;
		padding: 0 8px 2px 0;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	.assistant-title {
		font-weight: 500;
		font-size: 1.6rem;
		letter-spacing: 0.02em;
		white-space: nowrap;
	}
	.assistant-header-right {
		display: flex;
		align-items: center;
		gap: 2px;
		white-space: nowrap;
		min-width: 0;
	}
	// Header icon buttons read at the same weight as the title: 20px glyphs in near-black, not Vuetify's faint grey.
	.v-btn--icon .v-icon {
		font-size: 2rem;
		color: #424242;
	}
	.assistant-header-action {
		color: $assistant-muted;
		padding: 0 8px;
	}
}

.assistant-transcript {
	flex: 1 1 auto;
	overflow-y: auto;
	padding: 16px 16px 8px;
	// Focused only by script (after a suggested reply is tapped), so no ring.
	&:focus {
		outline: none;
	}
}

// Conversations panel: one row per conversation between thin rules, the tint on hover, actions muted on the right.
.assistant-list-pane {
	flex: 1 1 auto;
	overflow-y: auto;
	.assistant-list-error {
		margin: 10px 16px 0;
	}
}
.assistant-list {
	.assistant-list-toolbar {
		padding: 12px 16px;
		.v-input,
		input {
			font-size: 1.4rem;
		}
		.v-input .v-icon {
			font-size: 1.8rem;
			color: $assistant-muted;
		}
	}
	.assistant-list-empty {
		font-size: 1.3rem;
		color: $assistant-muted;
		padding: 8px 16px;
	}
	// Nested under the dense list so these beat Vuetify's .v-list--dense sizes.
	.v-list.v-list--dense {
		.v-list-item {
			padding: 0 8px 0 16px;
			border-bottom: 1px solid $assistant-rule;
			&:first-child {
				border-top: 1px solid $assistant-rule;
			}
			&:hover::before {
				opacity: 0;
			}
			&:hover {
				background: $assistant-tint;
			}
			&.assistant-list-current .v-list-item__title {
				font-weight: 500;
			}
		}
		.v-list-item__content {
			padding: 8px 0;
		}
		.v-list-item__title {
			font-size: 1.4rem;
			line-height: 1.4;
			color: $assistant-ink;
			.v-input,
			input {
				font-size: 1.4rem;
			}
			.v-input {
				margin-top: 0;
				padding-top: 0;
			}
		}
		.v-list-item__subtitle {
			font-size: 1.2rem;
			line-height: 1.4;
			font-weight: 400;
			color: $assistant-muted !important;
			margin-top: 2px;
		}
		.assistant-list-actions {
			flex-direction: row;
			gap: 2px;
			align-self: center;
			margin: 0 0 0 8px;
			.v-icon {
				font-size: 1.8rem;
				color: $assistant-muted;
			}
		}
		// With a pointer, the row actions appear on hover or keyboard focus so the list reads as titles first;
		// touch screens always show them.
		@media (hover: hover) {
			.assistant-list-actions {
				opacity: 0;
				transition: opacity 120ms ease;
			}
			.v-list-item:hover .assistant-list-actions,
			.v-list-item:focus-within .assistant-list-actions,
			.v-list-item:focus .assistant-list-actions {
				opacity: 1;
			}
		}
		.v-list-item.assistant-list-confirming,
		.v-list-item.assistant-list-confirming:hover {
			background: #fff6f6;
			cursor: default;
		}
	}
	> .v-btn {
		margin-top: 4px;
	}
}
// Tooltips render at the application root, outside the drawer, so they set their own sizes on the 10px root.
// Black on the drawer's white keeps them quiet; short labels, a long title wraps at a readable width.
// The inline delete confirmation, in place of the row it is about.
.assistant-confirm {
	flex: 1 1 auto;
	padding: 10px 0;
	.assistant-confirm-title {
		font-size: 1.4rem;
		font-weight: 500;
		color: $assistant-ink;
	}
	.assistant-confirm-text {
		font-size: 1.25rem;
		line-height: 1.45;
		color: $assistant-muted;
		margin: 2px 0 8px;
	}
	.assistant-confirm-actions {
		display: flex;
		justify-content: flex-end;
		gap: 6px;
	}
}

.v-tooltip__content.assistant-tooltip {
	font-size: 1.2rem;
	line-height: 1.35;
	padding: 4px 8px;
	max-width: 280px;
	border-radius: 4px;
	background: rgba(33, 33, 33, 0.94);
	color: #fff;
	letter-spacing: normal;
	word-break: break-word;
}

// Empty state: the invitation leads, the examples are the way in, the retention notice sits last and quiet.
.assistant-empty {
	padding-top: 8px;
	.assistant-empty-title {
		font-size: 2rem;
		font-weight: 500;
		line-height: 1.3;
		margin: 0 0 6px;
	}
	.assistant-empty-text {
		color: $assistant-muted;
		margin: 0 0 20px;
		max-width: 36em;
	}
	.assistant-empty-label {
		font-size: 1.2rem;
		color: $assistant-muted;
		margin: 0 0 6px;
	}
	.assistant-empty-notice {
		font-size: 1.2rem;
		line-height: 1.4;
		color: $assistant-muted;
		margin: 24px 0 0;
	}
}
.assistant-example {
	display: block;
	width: 100%;
	text-align: left;
	font: inherit;
	line-height: 1.4;
	color: $assistant-ink;
	background: #fff;
	border: 1px solid $assistant-rule;
	border-radius: 6px;
	padding: 8px 12px;
	margin-bottom: 6px;
	cursor: pointer;
	transition: background-color 120ms ease, border-color 120ms ease;
	&:hover {
		background: $assistant-tint;
		border-color: #c5d8f7;
	}
	&:focus-visible {
		outline: 2px solid $assistant-primary;
		outline-offset: 1px;
	}
}

// Messages: the user's words in the tinted block on the right, replies as plain text at full width.
.assistant-message {
	margin-bottom: 18px;
	&.user {
		display: flex;
		justify-content: flex-end;
	}
}
.assistant-query {
	max-width: 88%;
	padding: 8px 12px;
	border-radius: 10px 10px 2px 10px;
	background: $assistant-tint;
	color: $assistant-ink;
	white-space: pre-wrap;
	word-break: break-word;
}
.assistant-meta {
	display: flex;
	align-items: center;
	gap: 6px;
	font-size: 1.2rem;
	color: $assistant-muted;
	margin-bottom: 6px;
	min-height: 18px;
	.v-icon {
		font-size: 1.5rem;
		color: $assistant-muted;
	}
}
.assistant-working {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: $assistant-mark;
	flex: 0 0 auto;
	animation: assistant-pulse 1.2s ease-in-out infinite;
}
@keyframes assistant-pulse {
	0%,
	100% {
		opacity: 1;
		transform: scale(1);
	}
	50% {
		opacity: 0.35;
		transform: scale(0.75);
	}
}
@media (prefers-reduced-motion: reduce) {
	.assistant-working {
		animation: none;
	}
	.assistant-example,
	.assistant-suggestion {
		transition: none;
	}
}
.assistant-markdown {
	word-break: break-word;
	p {
		margin: 0 0 10px;
	}
	ul,
	ol {
		margin: 0 0 10px;
		padding-left: 2.2rem;
	}
	li {
		margin-bottom: 2px;
	}
	a {
		color: $assistant-primary !important;
	}
	code {
		font-size: 1.3rem;
		background: #f4f4f6;
		padding: 0 3px;
		border-radius: 3px;
		box-shadow: none;
	}
	// Tables are ruled the way the results grid is: a heavy rule under the header, light rules between rows, no
	// cell boxes. Wide tables scroll inside the drawer instead of stretching it.
	table {
		display: block;
		max-width: 100%;
		overflow-x: auto;
		border-collapse: collapse;
		margin: 4px 0 12px;
		font-size: 1.3rem;
		line-height: 1.35;
		th,
		td {
			padding: 5px 10px 5px 0;
			vertical-align: top;
			text-align: left;
			word-break: normal; // never split a vendor name or a unit; a wide table scrolls instead
			overflow-wrap: normal;
		}
		th {
			font-weight: 500;
			white-space: nowrap;
			border-bottom: 2px solid $assistant-ink;
			vertical-align: bottom;
		}
		td {
			border-bottom: 1px solid $assistant-rule;
		}
		th:last-child,
		td:last-child {
			padding-right: 0;
		}
	}
	> :last-child {
		margin-bottom: 0;
	}
}
// Suggested replies: the example prompt style, sized to the reply, wrapping on narrow widths.
.assistant-suggestions {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	margin-top: 12px;
}
.assistant-suggestion {
	max-width: 100%;
	text-align: left;
	font: inherit;
	font-size: 1.3rem;
	line-height: 1.35;
	color: $assistant-ink;
	background: #fff;
	border: 1px solid $assistant-rule;
	border-radius: 6px;
	padding: 6px 10px;
	word-break: break-word;
	cursor: pointer;
	transition: background-color 120ms ease, border-color 120ms ease;
	&:hover {
		background: $assistant-tint;
		border-color: #c5d8f7;
	}
	&:focus-visible {
		outline: 2px solid $assistant-primary;
		outline-offset: 1px;
	}
}
.assistant-truncated {
	font-size: 1.2rem;
	color: $assistant-muted;
	margin-top: 4px;
}

// Cards: the things the reply hands back to the page (a search, a document). Bordered, never shaded.
.assistant-card {
	margin-top: 10px;
	padding: 8px 12px;
	border: 1px solid $assistant-rule;
	border-radius: 6px;
	background: #fff;
	font-size: 1.3rem;
	.assistant-card-title {
		font-weight: 500;
		line-height: 1.4;
	}
	.assistant-card-note {
		color: $assistant-muted;
		font-weight: 400;
	}
	.assistant-card-action {
		margin: 4px 0 0 -8px;
	}
	.assistant-chips {
		margin-top: 6px;
	}
	.v-chip {
		color: $assistant-ink;
		border-color: #c5d8f7;
		background: $assistant-tint !important;
	}
}
.assistant-card-row {
	display: flex;
	align-items: center;
	gap: 8px;
	.assistant-card-title {
		flex: 1 1 auto;
		min-width: 0;
		word-break: break-word;
	}
	.assistant-card-action {
		margin: 0;
	}
}

// Composer: one bordered box holding the text and, on its bottom row, the allowance and the send control.
.assistant-composer {
	border-top: 1px solid $assistant-rule;
	padding: 10px 16px 12px;
	.assistant-composer-box {
		border: 1px solid #bdbdbd;
		border-radius: 8px;
		background: #fff;
		transition: border-color 120ms ease, box-shadow 120ms ease;
		&:focus-within {
			border-color: $assistant-primary;
			box-shadow: 0 0 0 1px $assistant-primary;
		}
	}
	.v-input {
		font-size: 1.5rem;
	}
	.v-input__slot {
		padding: 0 12px !important;
		min-height: 0 !important;
		background: transparent !important;
	}
	.v-text-field--solo .v-input__control {
		min-height: 0;
		background: transparent !important; // Vuetify paints the control white, which squares off the box's corners
	}
	.v-textarea textarea {
		margin: 8px 0 2px;
		padding: 0;
	}
	.v-input--is-disabled textarea {
		color: $assistant-muted;
	}
	.assistant-composer-footer {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;
		padding: 0 6px 6px 12px;
	}
	.assistant-usage {
		font-size: 1.2rem;
		color: $assistant-muted;
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		&:focus-visible {
			outline: 2px solid $assistant-primary;
			outline-offset: 2px;
			border-radius: 2px;
		}
	}
	.v-btn--fab.v-size--x-small {
		width: 30px;
		height: 30px;
	}
}

// Notices and errors: a rule on the left instead of a colour wash; red only when something failed.
.assistant-notice {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 4px 8px;
	font-size: 1.3rem;
	line-height: 1.4;
	padding: 6px 10px;
	border-left: 3px solid #9e9e9e;
	margin: 4px 0 8px;
	&.assistant-error {
		border-left-color: $assistant-mark;
	}
}
</style>
